import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { TYPE } from '@/lib/typography';

export interface FormBreadcrumbItem {
  label: string;
  to?: string;
}

/**
 * Full-height replacement for a long patient-record form. Not a modal: the
 * record tabs stay unmounted so they cannot paint over fields or steal clicks.
 */
export function PatientRecordFormPage({
  children,
  onCancel,
}: {
  children: ReactNode;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-ds-bg">
      <div className="mb-s4">
        <Button type="button" variant="tertiary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
      {children}
    </div>
  );
}

export function FormPage({
  breadcrumb,
  title,
  children,
  onCancel,
  primary,
  cancelLabel,
}: {
  breadcrumb: FormBreadcrumbItem[];
  title: ReactNode;
  children: ReactNode;
  onCancel: () => void;
  primary: ReactNode;
  cancelLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-24">
        <nav aria-label="Breadcrumb" className="mb-s3 flex flex-wrap items-center gap-s1 text-[13px]">
          {breadcrumb.map((item, index) => {
            const last = index === breadcrumb.length - 1;
            return (
              <span key={`${item.label}-${index}`} className="flex items-center gap-s1">
                {index > 0 ? <ChevronRight className="h-3.5 w-3.5 text-ds-muted" aria-hidden /> : null}
                {item.to && !last ? (
                  <Link to={item.to} className="font-medium text-ds-teal">
                    {item.label}
                  </Link>
                ) : (
                  <span className={last ? 'font-semibold text-ds-text' : 'text-ds-muted'}>{item.label}</span>
                )}
              </span>
            );
          })}
        </nav>
        <h1 className={cn(TYPE.title, 'text-ds-text')}>{title}</h1>
        <div className="mt-s5 flex flex-col gap-s5">{children}</div>
      </div>
      <div className="sticky bottom-0 z-10 flex items-center justify-end gap-s2 border-t border-ds-line bg-ds-bg/95 px-0 py-s4 backdrop-blur">
        <Button type="button" variant="tertiary" onClick={onCancel}>
          {cancelLabel ?? t('common.cancel')}
        </Button>
        {primary}
      </div>
    </div>
  );
}

export function FormSection({
  title,
  children,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-card bg-ds-card p-s5 shadow-card', className)}>
      {title ? <h2 className={cn(TYPE.heading, 'mb-s4 text-ds-text')}>{title}</h2> : null}
      {children}
    </section>
  );
}
